import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useHubStore, type DraftMomentPhoto } from "../use-hub-store";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { hubToast } from "../hub-toasts";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { EntityHeader } from "../entity-header";
import {
  usePresignedUrl,
  useAddImageToMoment,
  usePublishMoment,
} from "@/lib/api/shareable-entities";
import axios from "axios";
import { usePostHogEvent } from "@/lib/posthog";
import { createThumbnail } from "@/lib/utils/image";
import { useLightbox } from "../shared/use-lightbox";
import { Lightbox } from "../shared/lightbox";
import { ScheduleFooterButton } from "../scheduling/schedule-footer-button";

interface PreviewMomentViewProps {
  mentorship: MentorshipDetails;
  onSend?: () => Promise<void>;
}

export function PreviewMomentView({
  mentorship,
  onSend,
}: PreviewMomentViewProps) {
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const draftMoment = useHubStore((state) => state.draftMoment);
  const setDraftMoment = useHubStore((state) => state.setDraftMoment);
  const navigateToView = useHubStore((state) => state.navigateToView);

  const menteeId = mentorship.mentee.id;

  const presignedUrlMutation = usePresignedUrl();
  const addImageMutation = useAddImageToMoment(menteeId);
  const publishMoment = usePublishMoment(menteeId);
  const { dispatchEvent } = usePostHogEvent("mentor_hub_moment_shared", { onMount: false });

  const photos = draftMoment?.photos ?? [];
  const caption = draftMoment?.caption ?? "";

  const lightbox = useLightbox(photos.length);

  const handlePrevPhoto = () => {
    setActivePhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNextPhoto = () => {
    setActivePhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const uploadPhoto = async (photo: DraftMomentPhoto, draftId?: string) => {
    const [presignedData, thumbnail] = await Promise.all([
      presignedUrlMutation.mutateAsync({
        filename: photo.file.name,
        contentType: photo.file.type,
      }),
      createThumbnail(photo.file),
    ]);

    await Promise.all([
      axios.put(presignedData.highQuality.url, photo.file, {
        headers: { "Content-Type": photo.file.type },
      }),
      axios.put(presignedData.thumbnail.url, thumbnail, {
        headers: { "Content-Type": thumbnail.type },
      }),
    ]);

    const result = await addImageMutation.mutateAsync({
      key: presignedData.highQuality.key,
      url: presignedData.highQuality.url.split("?")[0],
      thumbnailKey: presignedData.thumbnail.key,
      thumbnailUrl: presignedData.thumbnail.url.split("?")[0],
      draftId,
    });

    return result;
  };

  const uploadPhotosAndGetDraftId = async () => {
    let draftId: string | undefined;

    for (const photo of photos) {
      const result = await uploadPhoto(photo, draftId);
      if (!draftId && result.momentId) {
        draftId = result.momentId;
      }
    }

    if (!draftId) {
      throw new Error("Failed to create moment draft");
    }

    return draftId;
  };

  const handleSend = async () => {
    if (photos.length === 0) {
      hubToast.error("No photos to send");
      return;
    }

    try {
      setIsUploading(true);

      const draftId = await uploadPhotosAndGetDraftId();

      await publishMoment.mutateAsync({
        momentId: draftId,
        params: {
          caption: caption || undefined,
          shareWithId: menteeId,
        },
      });

      dispatchEvent();
      await onSend?.();
      hubToast.sent("moment");
      setDraftMoment(null);
      navigateToView("list");
    } catch (error) {
      console.error("Failed to send moment:", error);
      hubToast.error("Failed to send moment. Please try again.");
    } finally {
      setIsUploading(false);
      setIsSendDialogOpen(false);
    }
  };

  const handleSchedule = async (scheduledAt: Date) => {
    if (photos.length === 0) {
      hubToast.error("No photos to schedule");
      return;
    }

    try {
      setIsUploading(true);

      const draftId = await uploadPhotosAndGetDraftId();

      await publishMoment.mutateAsync({
        momentId: draftId,
        params: {
          caption: caption || undefined,
          shareWithId: menteeId,
          schedule: { scheduleAt: scheduledAt.toISOString() },
        },
      });

      hubToast.scheduled("moment");
      setDraftMoment(null);
      navigateToView("list");
    } catch (error) {
      console.error("Failed to schedule moment:", error);
      hubToast.error("Failed to schedule moment. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="flex h-full flex-col bg-white">
        <EntityHeader title="Preview" showInfo={true} />
        <div className="flex-1 overflow-y-auto pt-4">
          {/* Photo Carousel - Horizontal scroll */}
          {photos.length > 0 && (
            <div className="relative mb-4">
              <div
                className="flex gap-2 px-4 transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(calc(-${activePhotoIndex * 85}% - ${activePhotoIndex * 8}px + ${activePhotoIndex > 0 ? "16px" : "0px"}))`,
                }}
              >
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => {
                      if (index === activePhotoIndex) {
                        lightbox.open(index);
                      } else {
                        setActivePhotoIndex(index);
                      }
                    }}
                    className={`relative h-[500px] flex-shrink-0 overflow-hidden rounded-2xl transition-opacity ${
                      photos.length === 1 ? "w-[calc(100%-32px)]" : "w-[85%]"
                    } ${index !== activePhotoIndex ? "opacity-70" : ""}`}
                  >
                    <img
                      src={photo.preview}
                      alt={`Moment photo ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Navigation arrows */}
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevPhoto}
                    disabled={activePhotoIndex === 0}
                    className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-50 disabled:opacity-30"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextPhoto}
                    disabled={activePhotoIndex === photos.length - 1}
                    className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-50 disabled:opacity-30"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          )}

          {/* Caption */}
          {caption && (
            <div className="mx-6 rounded-xl bg-[#F5F5F0] p-4">
              <p className="font-mono text-sm leading-relaxed text-gray-800">
                {caption}
              </p>
            </div>
          )}

          {/* Empty state */}
          {photos.length === 0 && (
            <div className="flex flex-1 items-center justify-center text-gray-400">
              No photos to preview
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="space-y-3 border-t border-gray-200 bg-white p-4">
          <ScheduleFooterButton
            entityType="moment"
            disabled={photos.length === 0 || isUploading}
            loading={isUploading}
            onSchedule={handleSchedule}
          />
          <Button
            onClick={() => setIsSendDialogOpen(true)}
            disabled={photos.length === 0 || isUploading}
            className="w-full rounded-full bg-black text-white hover:bg-gray-800"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              "Send"
            )}
          </Button>
        </div>
      </div>

      <ConfirmActionDialog
        isOpen={isSendDialogOpen}
        onClose={() => setIsSendDialogOpen(false)}
        onConfirm={handleSend}
        title="Are you sure you want to send Moment?"
        description="You won't be able to edit or delete the moment afterward."
        loading={isUploading || publishMoment.isPending}
        confirmVariant="default"
        confirmText="Send"
      />

      {lightbox.isOpen && (
        <Lightbox
          images={photos.map((p, i) => ({ url: p.preview, alt: `Moment photo ${i + 1}` }))}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.close}
          onPrev={lightbox.prev}
          onNext={lightbox.next}
        />
      )}
    </>
  );
}
